import { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { startBot, stopBot, getStatus } from "api/mt5SchedulerApi";
import { trackEvent } from "utils/analytics";
import { fmtDateTime } from "utils/datetime";

const MAX_SLOTS = 6;
const POLL_INTERVAL_MS = 3000;

function emptySlot() {
  return {
    symbol: "gold",
    entryDate: "",
    entryTime: "",
    exitDate: "",
    exitTime: "",
    side: "buy",
    lot: 0.01,
    enabled: true,
  };
}

function toTimestamp(dateStr, timeStr) {
  if (!dateStr || !timeStr) return NaN;
  return new Date(`${dateStr}T${timeStr}`).getTime();
}

function Mt5Scheduler() {
  // 連線設定
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [server, setServer] = useState("");

  // 6 組時段
  const [slots, setSlots] = useState(() =>
    Array.from({ length: MAX_SLOTS }, () => emptySlot())
  );

  const [status, setStatus] = useState({ running: false });
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const refreshStatus = useCallback(async () => {
    try {
      const res = await getStatus();
      setStatus(res.data?.data || { running: false });
    } catch (e) {
      // 不顯示輪詢的錯誤，避免噪音
    }
  }, []);

  useEffect(() => {
    refreshStatus();
    const id = setInterval(refreshStatus, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refreshStatus]);

  function updateSlot(i, key, value) {
    setSlots((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], [key]: value };
      return next;
    });
  }

  function buildPayload() {
    const enabledSlots = slots.filter((s) => s.enabled);
    const schedules = [];
    for (const s of enabledSlots) {
      const entryTs = toTimestamp(s.entryDate, s.entryTime);
      const exitTs = toTimestamp(s.exitDate, s.exitTime);
      if (Number.isNaN(entryTs) || Number.isNaN(exitTs)) {
        throw new Error("有啟用中的時段日期或時間沒填完整");
      }
      if (exitTs <= entryTs) {
        throw new Error("有啟用中的時段，出場時間早於或等於進場時間");
      }
      schedules.push({
        symbol: s.symbol,
        entryTs,
        exitTs,
        side: s.side,
        lot: Number(s.lot),
      });
    }
    if (schedules.length === 0) {
      throw new Error("至少要啟用 1 組時段");
    }
    return {
      account: Number(account),
      password,
      server,
      schedules,
    };
  }

  async function handleStart() {
    setErrMsg("");
    let payload;
    try {
      payload = buildPayload();
    } catch (e) {
      setErrMsg(e.message);
      return;
    }
    if (!payload.account || !payload.password || !payload.server) {
      setErrMsg("MT5 帳號 / 密碼 / 伺服器 必填");
      return;
    }
    setSubmitting(true);
    try {
      await startBot(payload);
      trackEvent("create_schedule", { slot_count: payload.schedules?.length || 0 });
      await refreshStatus();
    } catch (e) {
      setErrMsg(e.response?.data?.message || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleStop() {
    setErrMsg("");
    setSubmitting(true);
    try {
      await stopBot();
      await refreshStatus();
    } catch (e) {
      setErrMsg(e.response?.data?.message || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  const running = !!status.running;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          {/* === Header === */}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <MDTypography variant="h6" color="white">
                  MT5 定時下單（XM 黃金 / 白銀）
                </MDTypography>
                <Chip
                  label={running ? "RUNNING" : "STOPPED"}
                  color={running ? "success" : "default"}
                  size="small"
                />
              </MDBox>

              <MDBox p={3}>
                {/* === 連線設定 === */}
                <MDTypography variant="subtitle2" mb={1}>
                  MT5 連線（密碼不會儲存，伺服器重啟即失效）
                </MDTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="帳號"
                      type="number"
                      fullWidth
                      value={account}
                      onChange={(e) => setAccount(e.target.value)}
                      disabled={running || submitting}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="密碼"
                      type="password"
                      fullWidth
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={running || submitting}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="伺服器"
                      placeholder="例如 XMGlobal-MT5"
                      fullWidth
                      value={server}
                      onChange={(e) => setServer(e.target.value)}
                      disabled={running || submitting}
                    />
                  </Grid>
                </Grid>

                {/* === 6 組時段 === */}
                <MDBox mt={4} mb={1}>
                  <MDTypography variant="subtitle2">
                    進出場時段（最多 6 組，未勾選的不會生效；時間以使用者本機時區為準）
                  </MDTypography>
                </MDBox>
                <Box sx={{ minWidth: 1080 }}>
                  {/* 表頭 */}
                  <Box display="flex" alignItems="center" gap={1.5} px={1} py={1} sx={{ borderBottom: 1, borderColor: "divider", fontWeight: 600 }}>
                    <Box sx={{ width: 30, textAlign: "center" }}>#</Box>
                    <Box sx={{ width: 50, textAlign: "center" }}>啟用</Box>
                    <Box sx={{ flex: 1.3 }}>商品</Box>
                    <Box sx={{ flex: 2.5 }}>進場日期</Box>
                    <Box sx={{ flex: 1.8 }}>進場時間</Box>
                    <Box sx={{ flex: 2.5 }}>出場日期</Box>
                    <Box sx={{ flex: 1.8 }}>出場時間</Box>
                    <Box sx={{ flex: 1.3 }}>方向</Box>
                    <Box sx={{ flex: 1.2 }}>手數</Box>
                  </Box>

                  {/* 6 列 */}
                  {slots.map((s, i) => (
                    <Box
                      key={i}
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      px={1}
                      py={1}
                      sx={{ borderBottom: 1, borderColor: "divider" }}
                    >
                      <Box sx={{ width: 30, textAlign: "center" }}>{i + 1}</Box>
                      <Box sx={{ width: 50, textAlign: "center" }}>
                        <Checkbox
                          checked={s.enabled}
                          onChange={(e) => updateSlot(i, "enabled", e.target.checked)}
                          disabled={running || submitting}
                          sx={{ p: 0 }}
                        />
                      </Box>
                      <Box sx={{ flex: 1.3 }}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={s.symbol}
                          onChange={(e) => updateSlot(i, "symbol", e.target.value)}
                          disabled={running || submitting || !s.enabled}
                        >
                          <MenuItem value="gold">黃金</MenuItem>
                          <MenuItem value="silver">白銀</MenuItem>
                        </TextField>
                      </Box>
                      <Box sx={{ flex: 2.5 }}>
                        <TextField
                          type="date"
                          size="small"
                          fullWidth
                          value={s.entryDate}
                          onChange={(e) => updateSlot(i, "entryDate", e.target.value)}
                          disabled={running || submitting || !s.enabled}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                      <Box sx={{ flex: 1.8 }}>
                        <TextField
                          type="time"
                          size="small"
                          fullWidth
                          value={s.entryTime}
                          onChange={(e) => updateSlot(i, "entryTime", e.target.value)}
                          disabled={running || submitting || !s.enabled}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                      <Box sx={{ flex: 2.5 }}>
                        <TextField
                          type="date"
                          size="small"
                          fullWidth
                          value={s.exitDate}
                          onChange={(e) => updateSlot(i, "exitDate", e.target.value)}
                          disabled={running || submitting || !s.enabled}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                      <Box sx={{ flex: 1.8 }}>
                        <TextField
                          type="time"
                          size="small"
                          fullWidth
                          value={s.exitTime}
                          onChange={(e) => updateSlot(i, "exitTime", e.target.value)}
                          disabled={running || submitting || !s.enabled}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                      <Box sx={{ flex: 1.3 }}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={s.side}
                          onChange={(e) => updateSlot(i, "side", e.target.value)}
                          disabled={running || submitting || !s.enabled}
                        >
                          <MenuItem value="buy">buy</MenuItem>
                          <MenuItem value="sell">sell</MenuItem>
                        </TextField>
                      </Box>
                      <Box sx={{ flex: 1.2 }}>
                        <TextField
                          type="number"
                          size="small"
                          fullWidth
                          inputProps={{ step: 0.01, min: 0.01 }}
                          value={s.lot}
                          onChange={(e) => updateSlot(i, "lot", e.target.value)}
                          disabled={running || submitting || !s.enabled}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* === 操作 === */}
                <MDBox mt={3} display="flex" gap={2} alignItems="center">
                  {!running ? (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleStart}
                      disabled={submitting}
                    >
                      啟動 Bot
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleStop}
                      disabled={submitting}
                    >
                      停止 Bot
                    </Button>
                  )}
                  {errMsg && (
                    <MDTypography variant="caption" color="error">
                      {errMsg}
                    </MDTypography>
                  )}
                </MDBox>

                {/* === 狀態面板（基本版，Stage 5 會擴充 log）=== */}
                {running && (
                  <MDBox mt={3} p={2} bgcolor="grey.100" borderRadius={1}>
                    <MDTypography variant="caption" display="block">
                      啟動於：{fmtDateTime(status.startedAt, { withSeconds: true })}
                    </MDTypography>
                    <MDTypography variant="caption" display="block">
                      商品：{status.symbol} ／ 排程數：{status.scheduleCount}
                    </MDTypography>
                    {status.recentLogs?.length > 0 && (
                      <MDBox
                        mt={1}
                        sx={{
                          fontFamily: "monospace",
                          fontSize: 12,
                          whiteSpace: "pre-wrap",
                          maxHeight: 240,
                          overflow: "auto",
                        }}
                      >
                        {status.recentLogs.join("\n")}
                      </MDBox>
                    )}
                  </MDBox>
                )}

                {!running && status.lastSnapshot && (
                  <MDBox mt={3} p={2} bgcolor="grey.50" borderRadius={1}>
                    <MDTypography variant="caption" display="block" color="text.secondary">
                      上次執行：{fmtDateTime(status.lastSnapshot.startedAt, { withSeconds: true })} ~ {fmtDateTime(status.lastSnapshot.endedAt, { withSeconds: true })}
                      （exit code {status.lastSnapshot.exitCode}）
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Mt5Scheduler;
